import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import InputComponent from '../components/form/InputComponent';
import CheckboxComponent from '../components/form/CheckboxComponent';
import RadioButton from '../components/form/RadioButton';
import InfoTooltip from '../components/form/InfoTooltip';
import FloatingTemplateMenu from '../components/FloatingTemplateMenu';
import PDFPreviewModal from '../components/PDFPreviewModal';
import { useAuth } from '../contexts/AuthContext';
import { checkClientVersion } from '../utils/clientVersion';

const FormPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const params = useParams();
  const location = useLocation();
  const loadedTemplateId = useRef<string | null>(null);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isDevHost = typeof window !== 'undefined' && /^(dev-stilling\.pmsalg\.no|localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendAsana, setSendAsana] = useState(false);

  // Debug: Log user data specifically for email
  console.log('User object:', user);
  console.log('User email:', user?.email);
  console.log('Email field exists:', user && 'email' in user);

  // Get today's date in YYYY-MM-DD format for date inputs
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    // Campaign info
    annonsor: '',
    handlekurvnr: '',
    ordrenumre: [''],
    stillingstittel: '',
    arbeidsgiver: '',
    kampanjenavn: '',
    landingsside: '',
    landingPages: [''],
    ingenLandingsside: false,
    landingssideBeskrivelse: '',
    // Facebook/Instagram fields
    facebookInstagramEnabled: false,
    spendingTotalt: '',
    hvoravRetargeting: '',
    annonseformat: '',
    geotargeting: '',
    fra: getTodayDate(),
    til: '',
    // LinkedIn fields
    linkedinEnabled: false,
    linkedinSpendingTotalt: '',
    linkedinHvoravRetargeting: '',
    linkedinAnnonseformat: '',
    linkedinGeotargeting: '',
    linkedinMalstyring: '',
    linkedinFra: getTodayDate(),
    linkedinTil: '',
    // Andre kanaler
    andreKanalerEnabled: false,
    andreKanaler: [
      {
        kanal: '',
        spendingTotalt: '',
        hvoravRetargeting: '',
        annonseformat: '',
        geotargeting: '',
        malstyring: '',
        fra: getTodayDate(),
        til: ''
      }
    ],
    // Innlegg/feed-tekst fields
    kortTekstStilling: '',
    soknadsfrist: '',
    hvilkenKanalPosting: '',
    ovrigInfoOppdrag: '',
    // Grafisk Produksjon fields
    grafiskProduksjon: [],
    rosenumre: [''],
    displayTyper: [],
    displayformat: [],
    stillingskarusellFra: '',
    stillingskarusellTil: '',
    skalPaMn24: false,
    hvissAnnet: '',
    // Korrektur Mail fields
    korrekturDato: '',
    korrekturEpost: '',
    // Rapport fields
    rapportKunde: false,
    rapportSelger: false,
    // Selger data
    selger: {
      fornavn: user?.firstName || '',
      etternavn: user?.lastName || '',
      epost: user?.email || '',
      rolle: user?.role || '',
      id: user?.id || ''
    }
  });


  // Auto-generate campaign name based on required fields
  useEffect(() => {
    const { ordrenumre, annonsor, arbeidsgiver, stillingstittel } = formData;
    const firstOrdrenr = ordrenumre[0] || '';

    // Only require annonsor, ordrenr, and stillingstittel (arbeidsgiver is optional)
    if (firstOrdrenr && annonsor && stillingstittel) {
      const parts = [firstOrdrenr, annonsor];

      // Add arbeidsgiver if it's filled and not similar to ordrenr or annonsor
      if (arbeidsgiver && arbeidsgiver.trim() !== '') {
        // Normalize strings for comparison (lowercase, remove special chars and spaces)
        const normalizeString = (str: string) =>
          str.toLowerCase().replace(/[^a-z0-9]/g, '');

        const normalizedOrdrenr = normalizeString(firstOrdrenr);
        const normalizedAnnonsor = normalizeString(annonsor);
        const normalizedArbeidsgiver = normalizeString(arbeidsgiver);

        // Only add arbeidsgiver if it's not similar to ordrenr or annonsor
        // Check if arbeidsgiver is different from both ordrenr and annonsor
        const isDifferentFromOrdrenr = !normalizedOrdrenr.includes(normalizedArbeidsgiver) &&
          !normalizedArbeidsgiver.includes(normalizedOrdrenr);
        const isDifferentFromAnnonsor = !normalizedAnnonsor.includes(normalizedArbeidsgiver) &&
          !normalizedArbeidsgiver.includes(normalizedAnnonsor);

        // Only add if different from both
        if (isDifferentFromOrdrenr && isDifferentFromAnnonsor) {
          parts.push(arbeidsgiver);
        }
      }

      // Always add stillingstittel at the end
      parts.push(stillingstittel);

      const generatedName = parts.join(' - ');
      setFormData(prev => ({
        ...prev,
        kampanjenavn: generatedName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        kampanjenavn: ''
      }));
    }
  }, [formData.ordrenumre, formData.annonsor, formData.arbeidsgiver, formData.stillingstittel]);

  // Ensure date fields have today's date when sections are enabled
  useEffect(() => {
    const updates: any = {};

    if (formData.facebookInstagramEnabled && !formData.fra) {
      updates.fra = getTodayDate();
    }

    if (formData.linkedinEnabled && !formData.linkedinFra) {
      updates.linkedinFra = getTodayDate();
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [formData.facebookInstagramEnabled, formData.linkedinEnabled, formData.fra, formData.linkedinFra]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // Update selger data when user changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        selger: {
          fornavn: user.firstName || '',
          etternavn: user.lastName || '',
          epost: user.email || '',
          rolle: user.role || '',
          id: user.id || ''
        }
      }));
    }
  }, [user]);

  // Load template if URL contains template ID
  useEffect(() => {
    const loadTemplateFromUrl = async () => {
      console.log('Loading template from URL, params:', params, 'location:', location);

      // Get template ID from URL params or query string
      const templateId = params.templateId || new URLSearchParams(location.search).get('template');

      // Prevent loading same template multiple times
      if (templateId && user && loadedTemplateId.current !== templateId) {
        console.log('Loading template with ID:', templateId);
        loadedTemplateId.current = templateId;

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/templates/${templateId}`, {
            credentials: 'include'
          });

          if (response.ok) {
            const template = await response.json();
            console.log('Template loaded successfully:', template);
            handleLoadTemplate(template.formData);
          } else {
            const error = await response.json();
            console.error('Failed to load template from URL:', error);
            loadedTemplateId.current = null; // Reset flag on error
          }
        } catch (error) {
          console.error('Error loading template from URL:', error);
          loadedTemplateId.current = null; // Reset flag on error
        }
      } else if (!templateId) {
        // Reset flag when there's no template ID (regular form page)
        loadedTemplateId.current = null;
        console.log('No template ID found or user not loaded:', { templateId, user: !!user });
      }
    };

    if (user && !isLoading) {
      loadTemplateFromUrl();
    }
  }, [params.templateId, location.search, user, isLoading]);

  const handleCheckboxArrayChange = (name: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
        ? [...(prev[name as keyof typeof prev] as string[]), value]
        : (prev[name as keyof typeof prev] as string[]).filter(item => item !== value)
    }));
  };

  // Handle ordrenummer changes
  const handleOrdrenummerChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      ordrenumre: prev.ordrenumre.map((ordrenr, i) =>
        i === index ? value : ordrenr
      )
    }));
  };

  // Add new ordrenummer field
  const addOrdrenummer = () => {
    setFormData(prev => ({
      ...prev,
      ordrenumre: [...prev.ordrenumre, '']
    }));
  };

  // Remove ordrenummer field
  const removeOrdrenummer = (index: number) => {
    if (formData.ordrenumre.length > 1) {
      setFormData(prev => ({
        ...prev,
        ordrenumre: prev.ordrenumre.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle landing page changes
  const handleLandingPageChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      landingPages: prev.landingPages.map((page, i) =>
        i === index ? value : page
      )
    }));
  };

  // Add new landing page field
  const addLandingPage = () => {
    setFormData(prev => ({
      ...prev,
      landingPages: [...prev.landingPages, '']
    }));
  };

  // Remove landing page field
  const removeLandingPage = (index: number) => {
    if (formData.landingPages.length > 1) {
      setFormData(prev => ({
        ...prev,
        landingPages: prev.landingPages.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle rosenummer changes
  const handleRosenummerChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      rosenumre: (prev.rosenumre || ['']).map((rosenr, i) =>
        i === index ? value : rosenr
      )
    }));
  };

  // Add new rosenummer field
  const addRosenummer = () => {
    setFormData(prev => ({
      ...prev,
      rosenumre: [...(prev.rosenumre || ['']), '']
    }));
  };

  // Remove rosenummer field
  const removeRosenummer = (index: number) => {
    const currentRosenumre = formData.rosenumre || [''];
    if (currentRosenumre.length > 1) {
      setFormData(prev => ({
        ...prev,
        rosenumre: (prev.rosenumre || ['']).filter((_, i) => i !== index)
      }));
    }
  };

  // Handle andre kanaler input changes
  const handleAndreKanalerChange = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      andreKanaler: prev.andreKanaler.map((kanal, i) =>
        i === index ? { ...kanal, [field]: value } : kanal
      )
    }));
  };

  // Handle andre kanaler radio button changes
  const handleAndreKanalerRadioChange = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      andreKanaler: prev.andreKanaler.map((kanal, i) =>
        i === index ? {
          ...kanal,
          [field]: value
        } : kanal
      )
    }));
  };

  // Handle grafisk produksjon date changes with validation
  const handleGrafiskDateChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'stillingskarusellFra') {
      const tilInput = document.querySelector('input[name="stillingskarusellTil"]') as HTMLInputElement;
      if (tilInput && value) {
        tilInput.min = value;
        // Clear "til" if it's before the new "fra" date
        if (formData.stillingskarusellTil && formData.stillingskarusellTil < value) {
          setFormData(prev => ({ ...prev, stillingskarusellTil: '' }));
        }
      }
    }
  };

  // Add new andre kanal
  const addAndreKanal = () => {
    setFormData(prev => ({
      ...prev,
      andreKanaler: [...prev.andreKanaler, {
        kanal: '',
        spendingTotalt: '',
        hvoravRetargeting: '',
        annonseformat: '',
        geotargeting: '',
        malstyring: '',
        fra: getTodayDate(),
        til: ''
      }]
    }));
  };

  // Remove andre kanal
  const removeAndreKanal = (index: number) => {
    if (formData.andreKanaler.length > 1) {
      setFormData(prev => ({
        ...prev,
        andreKanaler: prev.andreKanaler.filter((_, i) => i !== index)
      }));
    }
  };

  // Template save functionality
  const handleSaveTemplate = async () => {
    const templateName = prompt('Ange ett navn för templaten:');
    if (!templateName || templateName.trim() === '') {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: templateName.trim(),
          formData: formData,
          isPublic: false,
          tags: []
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Template "${templateName}" har blitt lagret!`);
        console.log('Template saved successfully:', data.template);
      } else {
        const error = await response.json();
        console.error('Backend error response:', error);
        console.error('Validation errors:', error.errors);
        const errorMessage = error.errors && error.errors.length > 0
          ? `${error.message}: ${error.errors.join(', ')}`
          : error.message || 'Failed to save template';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Det oppstod en feil ved lagring av template: ${errorMessage}`);
    }
  };

  // Template load functionality
  const handleLoadTemplate = (templateData: any) => {
    try {
      const today = getTodayDate();

      // Update form data with template data, but keep seller info from current user
      const updatedFormData = {
        ...templateData,
        selger: {
          fornavn: user?.firstName || '',
          etternavn: user?.lastName || '',
          epost: user?.email || '',
          rolle: user?.role || '',
          id: user?.id || ''
        },
        // Ensure rosenumre is always an array - handle migration from old rosenummer field
        rosenumre: templateData.rosenumre || (templateData.rosenummer ? [templateData.rosenummer] : [''])
      };

      // Update any date fields that are in the past to today's date
      const dateFields = ['fra', 'til', 'linkedinFra', 'linkedinTil', 'soknadsfrist', 'korrekturDato', 'stillingskarusellFra', 'stillingskarusellTil'];

      dateFields.forEach(field => {
        if (updatedFormData[field] && updatedFormData[field] < today) {
          console.log(`Updating past date field '${field}' from ${updatedFormData[field]} to ${today}`);
          updatedFormData[field] = today;
        }
      });

      // Update date fields in andreKanaler array
      if (updatedFormData.andreKanaler && Array.isArray(updatedFormData.andreKanaler)) {
        updatedFormData.andreKanaler.forEach((kanal: any, index: number) => {
          if (kanal.fra && kanal.fra < today) {
            console.log(`Updating past date field 'andreKanaler[${index}].fra' from ${kanal.fra} to ${today}`);
            kanal.fra = today;
          }
          if (kanal.til && kanal.til < today) {
            console.log(`Updating past date field 'andreKanaler[${index}].til' from ${kanal.til} to ${today}`);
            kanal.til = today;
          }
        });
      }

      setFormData(updatedFormData);
      alert('Template har blitt lastet inn! Gamle datoer har blitt oppdatert til dagens dato.');
    } catch (error) {
      console.error('Error loading template:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Det oppstod en feil ved lastning av template: ${errorMessage}`);
    }
  };

  // PDF Preview functionality
  const handlePDFPreview = () => {
    setShowPDFPreview(true);
  };

  // Form reset functionality
  const handleFormReset = () => {
    const initialFormData = {
      // Campaign info
      annonsor: '',
      handlekurvnr: '',
      ordrenumre: [''],
      stillingstittel: '',
      arbeidsgiver: '',
      kampanjenavn: '',
      landingsside: '',
      landingPages: [''],
      ingenLandingsside: false,
      landingssideBeskrivelse: '',
      // Facebook/Instagram fields
      facebookInstagramEnabled: false,
      spendingTotalt: '',
      hvoravRetargeting: '',
      annonseformat: '',
      geotargeting: '',
      fra: getTodayDate(),
      til: '',
      // LinkedIn fields
      linkedinEnabled: false,
      linkedinSpendingTotalt: '',
      linkedinHvoravRetargeting: '',
      linkedinAnnonseformat: '',
      linkedinGeotargeting: '',
      linkedinMalstyring: '',
      linkedinFra: getTodayDate(),
      linkedinTil: '',
      // Andre kanaler
      andreKanalerEnabled: false,
      andreKanaler: [
        {
          kanal: '',
          spendingTotalt: '',
          hvoravRetargeting: '',
          annonseformat: '',
          geotargeting: '',
          malstyring: '',
          fra: getTodayDate(),
          til: ''
        }
      ],
      // Innlegg/feed-tekst fields
      kortTekstStilling: '',
      soknadsfrist: '',
      hvilkenKanalPosting: '',
      ovrigInfoOppdrag: '',
      // Grafisk Produksjon fields
      grafiskProduksjon: [],
      rosenumre: [''],
      displayTyper: [],
      displayformat: [],
      stillingskarusellFra: '',
      stillingskarusellTil: '',
      skalPaMn24: false,
      hvissAnnet: '',
      // Korrektur Mail fields
      korrekturDato: '',
      korrekturEpost: '',
      // Rapport fields
      rapportKunde: false,
      rapportSelger: false,
      // Selger data
      selger: {
        fornavn: user?.firstName || '',
        etternavn: user?.lastName || '',
        epost: user?.email || '',
        rolle: user?.role || '',
        id: user?.id || ''
      }
    };

    setFormData(initialFormData);
    setIsSubmitted(false);
  };

  // Form submission functionality
  const handleFormSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          sendEmail: isDevHost ? sendEmail : true,
          sendAsana: isDevHost ? sendAsana : true
        })
      });

      if (response.ok) {
        const result = await response.json();
        const details = [];
        if (result.emailSent) details.push('e-post sendt');
        else if (result.emailSkipped) details.push('e-post hoppet over');
        if (result.asanaTaskId) details.push('Asana opprettet');
        else if (result.asanaSkipped) details.push('Asana hoppet over');
        alert(details.length
          ? `Skjema er sendt inn (${details.join(', ')}).`
          : 'Skjema er sendt inn! Du vil motta en bekreftelse via e-post.');
        console.log('Form submitted successfully:', result);
        setIsSubmitted(true);

        // Optionally redirect or reset form
        // navigate('/success');
      } else {
        const error = await response.json();
        console.error('Form submission error:', error);

        if (error.errors && Array.isArray(error.errors)) {
          const errorMessages = error.errors.join('\n');
          alert(`Feil i skjema:\n\n${errorMessages}`);
        } else {
          alert(`Feil ved innsending: ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Det oppstod en feil ved innsending av skjema: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Basic form validation
  const validateForm = () => {
    const requiredFields = [
      { field: 'annonsor', label: 'Annonsør' },
      { field: 'stillingstittel', label: 'Stillingstittel' },
      { field: 'arbeidsgiver', label: 'Arbeidsgiver' },
      { field: 'korrekturDato', label: 'Korrektur dato' },
      { field: 'korrekturEpost', label: 'Korrektur e-post' }
    ];

    let missingFields = requiredFields.filter(({ field }) => !formData[field as keyof typeof formData] || formData[field as keyof typeof formData] === '');

    // Check if at least one ordrenummer is filled
    const hasOrdrenummer = formData.ordrenumre.some(ordrenr => ordrenr && ordrenr.trim() !== '');
    if (!hasOrdrenummer) {
      missingFields.push({ field: 'ordrenumre', label: 'Minst ett ordrenummer' });
    }

    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(({ label }) => label).join(', ');
      alert(`Følgende påkrevde felt mangler: ${fieldNames}`);
      return false;
    }

    // Validate conditional fields
    if (formData.facebookInstagramEnabled) {
      const fbFields = [
        { field: 'spendingTotalt', label: 'Facebook spending totalt' },
        { field: 'geotargeting', label: 'Facebook geotargeting' },
        { field: 'fra', label: 'Facebook fra-dato' },
        { field: 'til', label: 'Facebook til-dato' }
      ];

      const missingFbFields = fbFields.filter(({ field }) => !formData[field as keyof typeof formData]);
      if (missingFbFields.length > 0) {
        const fieldNames = missingFbFields.map(({ label }) => label).join(', ');
        alert(`Facebook-kampanje er aktivert, men følgende felt mangler: ${fieldNames}`);
        return false;
      }

      if (!formData.annonseformat || formData.annonseformat === '') {
        alert('Facebook-kampanje er aktivert, men ingen annonseformat er valgt');
        return false;
      }
    }

    if (formData.linkedinEnabled) {
      const linkedinFields = [
        { field: 'linkedinSpendingTotalt', label: 'LinkedIn spending totalt' },
        { field: 'linkedinGeotargeting', label: 'LinkedIn geotargeting' },
        { field: 'linkedinFra', label: 'LinkedIn fra-dato' },
        { field: 'linkedinTil', label: 'LinkedIn til-dato' }
      ];

      const missingLinkedinFields = linkedinFields.filter(({ field }) => !formData[field as keyof typeof formData]);
      if (missingLinkedinFields.length > 0) {
        const fieldNames = missingLinkedinFields.map(({ label }) => label).join(', ');
        alert(`LinkedIn-kampanje er aktivert, men følgende felt mangler: ${fieldNames}`);
        return false;
      }

      if (!formData.linkedinAnnonseformat || formData.linkedinAnnonseformat === '') {
        alert('LinkedIn-kampanje er aktivert, men ingen annonseformat er valgt');
        return false;
      }
    }

    return true;
  };

  const handleSubmitWithValidation = async () => {
    if (!(await checkClientVersion())) return;
    if (validateForm()) {
      const confirmSubmit = window.confirm(
        'Er du sikker på at du vil sende inn skjemaet? Dette vil generere en PDF og sende den via e-post.'
      );

      if (confirmSubmit) {
        handleFormSubmit();
      }
    }
  };


  // Show loading state while authentication is being checked
  if (isLoading) {
    console.log('FormPage: Showing loading state');
    return (
      <div className="form-page">
        <Header onFormReset={handleFormReset} />
        <div className="form-page-content">
          <div className="container">
            <h1>Laster...</h1>
            <p>Henter brukerinformasjon...</p>
          </div>
        </div>
      </div>
    );
  }

  console.log('FormPage: Rendering main form, user:', user);

  return (
    <div className="form-page">
      <Header onFormReset={handleFormReset} />
      <div className="form-page-content">
        <div className="container">
          <h1>Stillingsskjema</h1>

          <form className="sales-form">
            {/* Seller Information Section */}
            <div className="form-section">
              <h2 className="section-title">Selger</h2>

              <div className="form-row">
                <div className="form-col third">
                  <InputComponent
                    label="Fornavn"
                    name="selgerFornavn"
                    value={user?.firstName || ''}
                    onChange={() => { }} // Read-only field
                    placeholder="Fornavn"
                    disabled
                  />
                </div>
                <div className="form-col third">
                  <InputComponent
                    label="Etternavn"
                    name="selgerEtternavn"
                    value={user?.lastName || ''}
                    onChange={() => { }} // Read-only field
                    placeholder="Etternavn"
                    disabled
                  />
                </div>
                <div className="form-col third">
                  <InputComponent
                    label="E-post"
                    type="email"
                    name="selgerEpost"
                    value={user?.email || ''}
                    onChange={() => { }} // Read-only field
                    placeholder="epost@example.com"
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Campaign Information Section */}
            <div className="form-section">
              <h2 className="section-title">Kampanjeinfo</h2>

              <div className="form-row">
                <div className="form-col half">
                  <InputComponent
                    label="Annonsør"
                    name="annonsor"
                    value={formData.annonsor}
                    onChange={handleInputChange}
                    placeholder="Navn på annonsør"
                    required
                  />
                </div>
                <div className="form-col half">
                  <InputComponent
                    label="Handlekurvnr"
                    type="number"
                    name="handlekurvnr"
                    value={formData.handlekurvnr}
                    onChange={handleInputChange}
                    placeholder="123456"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  {/* <h4 style={{ marginBottom: '16px', color: '#005379' }}>
                    Ordrenr CrossAd <span style={{ color: '#dc3545' }}>*</span>
                  </h4> */}
                  {formData.ordrenumre.map((ordrenr, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <InputComponent
                          label={index === 0 ? 'Ordrenr CrossAd' : `Ekstra ordrenummer ${index}`}
                          name={`ordrenummer-${index}`}
                          value={ordrenr}
                          onChange={(e) => handleOrdrenummerChange(index, e.target.value)}
                          placeholder={index === 0 ? '12345, 34543534, 45345345' : '123456'}
                          required={index === 0}
                        />
                      </div>
                      {formData.ordrenumre.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeOrdrenummer(index)}
                          style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            height: '40px',
                            marginTop: '10px',
                            flexShrink: 0
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOrdrenummer}
                    style={{
                      background: '#005379',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ➕ Legg til ekstra ordrenummer
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-col half">
                  <InputComponent
                    label="Stillingstittel"
                    name="stillingstittel"
                    value={formData.stillingstittel}
                    onChange={handleInputChange}
                    placeholder="Tittel på stillingen"
                    required
                  />
                </div>
                <div className="form-col half">
                  <InputComponent
                    label="Arbeidsgiver"
                    name="arbeidsgiver"
                    value={formData.arbeidsgiver}
                    onChange={handleInputChange}
                    placeholder="Navn på arbeidsgiver"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  <InputComponent
                    label="Kampanjenavn"
                    name="kampanjenavn"
                    value={formData.kampanjenavn}
                    onChange={() => { }} // Read-only field
                    placeholder="Genereres automatisk..."
                    disabled
                  />
                </div>
              </div>

              {/* Multiple Landing Pages */}
              <div className="form-section">
                <h3>Link til landingssider</h3>
                {formData.landingPages.map((page, index) => (
                  <div key={index} className="form-row" style={{ marginBottom: '12px' }}>
                    <div className="form-col" style={{ flex: '1' }}>
                      <InputComponent
                        label={`Landingsside ${index + 1}`}
                        type="url"
                        name={`landingPages_${index}`}
                        value={page}
                        onChange={(e) => handleLandingPageChange(index, e.target.value)}
                        placeholder="https://example.com/landing-page"
                        disabled={formData.ingenLandingsside}
                      />
                    </div>
                    {formData.landingPages.length > 1 && (
                      <div style={{ marginLeft: '10px', alignSelf: 'flex-end', marginBottom: '8px' }}>
                        <button
                          type="button"
                          onClick={() => removeLandingPage(index)}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Fjern
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={addLandingPage}
                    style={{
                      background: '#005379',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ➕ Legg til landingsside
                  </button>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <CheckboxComponent
                    label="Ingen landingsside enda"
                    name="ingenLandingsside"
                    value="true"
                    checked={formData.ingenLandingsside}
                    onChange={(e) => {
                      handleInputChange(e);
                      // Clear all landing page URLs when checkbox is checked
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, landingsside: '', landingPages: [''] }));
                      } else {
                        // Clear beskrivelse when unchecking
                        setFormData(prev => ({ ...prev, landingssideBeskrivelse: '' }));
                      }
                    }}
                  />
                </div>
              </div>

              {formData.ingenLandingsside && (
                <div className="form-row">
                  <div className="form-col full">
                    <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                      Beskrivelse
                    </label>
                    <textarea
                      name="landingssideBeskrivelse"
                      value={formData.landingssideBeskrivelse}
                      onChange={handleInputChange}
                      placeholder="Beskriv landingssiden som skal lages eller annen relevant informasjon..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '16px',
                        resize: 'vertical',
                        minHeight: '100px'
                      }}
                    />
                  </div>
                </div>
              )}

            </div>

            {/* Mål og målstyring Section */}
            <div className="form-section">
              <h2 className="section-title">Mål og målstyring</h2>

              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                Facebook / Instagram
              </h3>

              <div className="form-row">
                <div className="form-col full">
                  <CheckboxComponent
                    label="Aktiver Facebook / Instagram kampanje"
                    name="facebookInstagramEnabled"
                    value="facebook"
                    checked={formData.facebookInstagramEnabled}
                    onChange={(e) => handleInputChange(e)}
                  />
                </div>
              </div>

              {formData.facebookInstagramEnabled && (
                <>
                  <div className="form-row">
                    <div className="form-col half">
                      <InputComponent
                        label="Spending totalt"
                        type="number"
                        name="spendingTotalt"
                        value={formData.spendingTotalt}
                        onChange={handleInputChange}
                        placeholder="50000"
                        required
                      />
                    </div>
                    <div className="form-col half">
                      <InputComponent
                        label="Hvorav retargeting"
                        type="number"
                        name="hvoravRetargeting"
                        value={formData.hvoravRetargeting}
                        onChange={handleInputChange}
                        placeholder="10000"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <h4 style={{ marginBottom: '16px', color: '#005379' }}>
                        Annonseformat <span style={{ color: '#dc3545' }}>*</span>
                      </h4>
                      <div style={{ display: 'flex', gap: '20px' }}>
                        <RadioButton
                          label="Bilde"
                          name="annonseformat"
                          value="bilde"
                          checked={formData.annonseformat === 'bilde'}
                          onChange={handleInputChange}
                        />
                        <RadioButton
                          label="Video"
                          name="annonseformat"
                          value="video"
                          checked={formData.annonseformat === 'video'}
                          onChange={handleInputChange}
                        />
                        <RadioButton
                          label="Karusell"
                          name="annonseformat"
                          value="karusell"
                          checked={formData.annonseformat === 'karusell'}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <InputComponent
                        label="Geotargeting"
                        name="geotargeting"
                        value={formData.geotargeting}
                        onChange={handleInputChange}
                        placeholder="Oslo, Bergen, Trondheim"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col half">
                      <InputComponent
                        label="Fra"
                        type="date"
                        name="fra"
                        value={formData.fra}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Update minimum date for "til" field
                          const tilInput = document.querySelector('input[name="til"]') as HTMLInputElement;
                          if (tilInput && e.target.value) {
                            tilInput.min = e.target.value;
                            // Clear "til" if it's before the new "fra" date
                            if (formData.til && formData.til < e.target.value) {
                              setFormData(prev => ({ ...prev, til: '' }));
                            }
                          }
                        }}
                        min={getTodayDate()}
                        required
                      />
                    </div>
                    <div className="form-col half">
                      <InputComponent
                        label="Til"
                        type="date"
                        name="til"
                        value={formData.til}
                        onChange={handleInputChange}
                        min={formData.fra || getTodayDate()}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* LinkedIn Section */}
              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                LinkedIn
              </h3>

              <div className="form-row">
                <div className="form-col full">
                  <CheckboxComponent
                    label="Aktiver LinkedIn kampanje"
                    name="linkedinEnabled"
                    value="linkedin"
                    checked={formData.linkedinEnabled}
                    onChange={(e) => handleInputChange(e)}
                  />
                </div>
              </div>

              {formData.linkedinEnabled && (
                <>
                  <div className="form-row">
                    <div className="form-col half">
                      <InputComponent
                        label="Spending totalt"
                        type="number"
                        name="linkedinSpendingTotalt"
                        value={formData.linkedinSpendingTotalt}
                        onChange={handleInputChange}
                        placeholder="50000"
                        required
                      />
                    </div>
                    <div className="form-col half">
                      <InputComponent
                        label="Hvorav retargeting"
                        type="number"
                        name="linkedinHvoravRetargeting"
                        value={formData.linkedinHvoravRetargeting}
                        onChange={handleInputChange}
                        placeholder="10000"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <h4 style={{ marginBottom: '16px', color: '#005379' }}>
                        Annonseformat <span style={{ color: '#dc3545' }}>*</span>
                      </h4>
                      <div style={{ display: 'flex', gap: '20px' }}>
                        <RadioButton
                          label="Bilde"
                          name="linkedinAnnonseformat"
                          value="bilde"
                          checked={formData.linkedinAnnonseformat === 'bilde'}
                          onChange={handleInputChange}
                        />
                        <RadioButton
                          label="Video"
                          name="linkedinAnnonseformat"
                          value="video"
                          checked={formData.linkedinAnnonseformat === 'video'}
                          onChange={handleInputChange}
                        />
                        <RadioButton
                          label="Karusell"
                          name="linkedinAnnonseformat"
                          value="karusell"
                          checked={formData.linkedinAnnonseformat === 'karusell'}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <InputComponent
                        label="Geotargeting"
                        name="linkedinGeotargeting"
                        value={formData.linkedinGeotargeting}
                        onChange={handleInputChange}
                        placeholder="Oslo, Bergen, Trondheim"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                        Målstyring
                      </label>
                      <textarea
                        name="linkedinMalstyring"
                        value={formData.linkedinMalstyring}
                        onChange={handleInputChange}
                        placeholder="Beskriv målstyringsinformasjon her..."
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '16px',
                          resize: 'vertical',
                          minHeight: '100px'
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col half">
                      <InputComponent
                        label="Fra"
                        type="date"
                        name="linkedinFra"
                        value={formData.linkedinFra}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Update minimum date for LinkedIn "til" field
                          const tilInput = document.querySelector('input[name="linkedinTil"]') as HTMLInputElement;
                          if (tilInput && e.target.value) {
                            tilInput.min = e.target.value;
                            // Clear "til" if it's before the new "fra" date
                            if (formData.linkedinTil && formData.linkedinTil < e.target.value) {
                              setFormData(prev => ({ ...prev, linkedinTil: '' }));
                            }
                          }
                        }}
                        min={getTodayDate()}
                        required
                      />
                    </div>
                    <div className="form-col half">
                      <InputComponent
                        label="Til"
                        type="date"
                        name="linkedinTil"
                        value={formData.linkedinTil}
                        onChange={handleInputChange}
                        min={formData.linkedinFra || getTodayDate()}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Andre kanaler Section */}
              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                Andre kanaler
              </h3>

              <div className="form-row">
                <div className="form-col full">
                  <CheckboxComponent
                    label="Aktiver andre kanaler"
                    name="andreKanalerEnabled"
                    value="andre"
                    checked={formData.andreKanalerEnabled}
                    onChange={(e) => handleInputChange(e)}
                  />
                </div>
              </div>

              {formData.andreKanalerEnabled && (
                <>
                  {formData.andreKanaler.map((kanal, index) => (
                    <div key={index} style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, color: '#005379', fontSize: '16px', fontWeight: '600' }}>
                          Kanal {index + 1}
                        </h4>
                        {formData.andreKanaler.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAndreKanal(index)}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '14px',
                              cursor: 'pointer'
                            }}
                          >
                            Fjern
                          </button>
                        )}
                      </div>

                      <div className="form-row">
                        <div className="form-col full">
                          <InputComponent
                            label="Kanal"
                            name={`kanal-${index}`}
                            value={kanal.kanal}
                            onChange={(e) => handleAndreKanalerChange(index, 'kanal', e.target.value)}
                            placeholder="F.eks TikTok, Snapchat, Youtube..."
                            required
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-col half">
                          <InputComponent
                            label="Spending totalt"
                            type="number"
                            name={`spendingTotalt-${index}`}
                            value={kanal.spendingTotalt}
                            onChange={(e) => handleAndreKanalerChange(index, 'spendingTotalt', e.target.value)}
                            placeholder="50000"
                            required
                          />
                        </div>
                        <div className="form-col half">
                          <InputComponent
                            label="Hvorav retargeting"
                            type="number"
                            name={`hvoravRetargeting-${index}`}
                            value={kanal.hvoravRetargeting}
                            onChange={(e) => handleAndreKanalerChange(index, 'hvoravRetargeting', e.target.value)}
                            placeholder="10000"
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-col full">
                          <h4 style={{ marginBottom: '16px', color: '#005379' }}>
                            Annonseformat <span style={{ color: '#dc3545' }}>*</span>
                          </h4>
                          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            <RadioButton
                              label="Bilde"
                              name={`annonseformat-${index}`}
                              value="bilde"
                              checked={kanal.annonseformat === 'bilde'}
                              onChange={(e) => handleAndreKanalerRadioChange(index, 'annonseformat', e.target.value)}
                            />
                            <RadioButton
                              label="Video"
                              name={`annonseformat-${index}`}
                              value="video"
                              checked={kanal.annonseformat === 'video'}
                              onChange={(e) => handleAndreKanalerRadioChange(index, 'annonseformat', e.target.value)}
                            />
                            <RadioButton
                              label="Karusell"
                              name={`annonseformat-${index}`}
                              value="karusell"
                              checked={kanal.annonseformat === 'karusell'}
                              onChange={(e) => handleAndreKanalerRadioChange(index, 'annonseformat', e.target.value)}
                            />
                            <RadioButton
                              label="Story"
                              name={`annonseformat-${index}`}
                              value="story"
                              checked={kanal.annonseformat === 'story'}
                              onChange={(e) => handleAndreKanalerRadioChange(index, 'annonseformat', e.target.value)}
                            />
                            <RadioButton
                              label="Annet"
                              name={`annonseformat-${index}`}
                              value="annet"
                              checked={kanal.annonseformat === 'annet'}
                              onChange={(e) => handleAndreKanalerRadioChange(index, 'annonseformat', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-col full">
                          <InputComponent
                            label="Geotargeting"
                            name={`geotargeting-${index}`}
                            value={kanal.geotargeting}
                            onChange={(e) => handleAndreKanalerChange(index, 'geotargeting', e.target.value)}
                            placeholder="Oslo, Bergen, Trondheim"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-col full">
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                            Målstyring
                          </label>
                          <textarea
                            name={`malstyring-${index}`}
                            value={kanal.malstyring}
                            onChange={(e) => handleAndreKanalerChange(index, 'malstyring', e.target.value)}
                            placeholder="Beskriv målstyringsinformasjon her..."
                            rows={4}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '16px',
                              resize: 'vertical',
                              minHeight: '100px'
                            }}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-col half">
                          <InputComponent
                            label="Fra"
                            type="date"
                            name={`fra-${index}`}
                            value={kanal.fra}
                            onChange={(e) => {
                              handleAndreKanalerChange(index, 'fra', e.target.value);
                              // Update minimum date for this channel's "til" field
                              const tilInput = document.querySelector(`input[name="til-${index}"]`) as HTMLInputElement;
                              if (tilInput && e.target.value) {
                                tilInput.min = e.target.value;
                                // Clear "til" if it's before the new "fra" date
                                if (kanal.til && kanal.til < e.target.value) {
                                  handleAndreKanalerChange(index, 'til', '');
                                }
                              }
                            }}
                            min={getTodayDate()}
                            required
                          />
                        </div>
                        <div className="form-col half">
                          <InputComponent
                            label="Til"
                            type="date"
                            name={`til-${index}`}
                            value={kanal.til}
                            onChange={(e) => handleAndreKanalerChange(index, 'til', e.target.value)}
                            min={kanal.fra || getTodayDate()}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="form-row">
                    <div className="form-col full">
                      <button
                        type="button"
                        onClick={addAndreKanal}
                        style={{
                          background: '#005379',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '12px 24px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          width: '100%',
                          marginBottom: '20px'
                        }}
                      >
                        Legg til en kanal
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Innlegg/feed-tekst Section */}
              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                Innlegg/feed-tekst
              </h3>

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Kort tekst om stillingen/feedtekst til innlegg
                  </label>
                  <textarea
                    name="kortTekstStilling"
                    value={formData.kortTekstStilling}
                    onChange={handleInputChange}
                    placeholder="Skriv en kort beskrivelse av stillingen eller tekst til feed-innlegg..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Søknadsfrist
                  </label>
                  <input
                    type="date"
                    name="soknadsfrist"
                    value={formData.soknadsfrist}
                    onChange={handleInputChange}
                    min={getTodayDate()}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Hvilken kanal skal det postes fra
                  </label>
                  <textarea
                    name="hvilkenKanalPosting"
                    value={formData.hvilkenKanalPosting}
                    onChange={handleInputChange}
                    placeholder="F.eks. Firmets Facebook-side, LinkedIn-profil, Instagram-konto..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'flex', alignItems: 'center', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Øvrig info for oppdraget
                    <InfoTooltip text="Fyll inn nødvendig informasjon for å utføre oppdraget. F.eks: ønsker til utforming, utdypende om målgruppen, hva er budskapet, hensikten med kampanjen, fargepalett som skal brukes. Ønsker om bruk av bilde. Organisk innlegg, retargeting ol. Hvis innlegg i SoMe skal postes fra annen kanal enn rekrutteringsbyrået/annonsør." />
                  </label>
                  <textarea
                    name="ovrigInfoOppdrag"
                    value={formData.ovrigInfoOppdrag}
                    onChange={handleInputChange}
                    placeholder="Tilleggsinformasjon, spesielle ønsker, noter..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>

              {/* Grafisk Produksjon Section */}
              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                Grafisk Produksjon
              </h3>

              {/* <div className="form-row">
                <div className="form-col full">
                  <InputComponent
                    label="Hva skal lages"
                    name="hvaSkalLages"
                    value={formData.hvaSkalLages}
                    onChange={handleInputChange}
                    placeholder="Beskriv hva som skal lages..."
                    required
                  />
                </div>
              </div> */}

              <div className="form-row">
                <div className="form-col full">
                  <h4 style={{ marginBottom: '16px', color: '#005379' }}>Hva skal lages?</h4>
                  <div className="checkbox-grid">
                    <CheckboxComponent
                      label="Display"
                      name="grafiskProduksjon"
                      value="display"
                      checked={(formData.grafiskProduksjon as string[]).includes('display')}
                      onChange={(e) => handleCheckboxArrayChange('grafiskProduksjon', 'display', e.target.checked)}
                    />
                    <CheckboxComponent
                      label="Stillingskarusell"
                      name="grafiskProduksjon"
                      value="stillingskarusell"
                      checked={(formData.grafiskProduksjon as string[]).includes('stillingskarusell')}
                      onChange={(e) => handleCheckboxArrayChange('grafiskProduksjon', 'stillingskarusell', e.target.checked)}
                    />
                    <CheckboxComponent
                      label="Stilling Premium"
                      name="grafiskProduksjon"
                      value="stillingPremium"
                      checked={(formData.grafiskProduksjon as string[]).includes('stillingPremium')}
                      onChange={(e) => handleCheckboxArrayChange('grafiskProduksjon', 'stillingPremium', e.target.checked)}
                    />
                    <CheckboxComponent
                      label="Ukens stilling"
                      name="grafiskProduksjon"
                      value="ukensStilling"
                      checked={(formData.grafiskProduksjon as string[]).includes('ukensStilling')}
                      onChange={(e) => handleCheckboxArrayChange('grafiskProduksjon', 'ukensStilling', e.target.checked)}
                    />
                    {/* <CheckboxComponent
                      label="Annet"
                      name="grafiskProduksjon"
                      value="annet"
                      checked={(formData.grafiskProduksjon as string[]).includes('annet')}
                      onChange={(e) => handleCheckboxArrayChange('grafiskProduksjon', 'annet', e.target.checked)}
                    /> */}
                  </div>
                </div>
              </div>

              {/* Display and Ukens stilling conditional fields */}
              {((formData.grafiskProduksjon as string[]).includes('display') || (formData.grafiskProduksjon as string[]).includes('ukensStilling')) && (
                <>
                  <div className="form-row">
                    <div className="form-col full">
                      {(formData.rosenumre || ['']).map((rosenummer, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <InputComponent
                              label={index === 0 ? 'Rosenummer' : `Ekstra rosenummer ${index}`}
                              type="number"
                              name={`rosenummer-${index}`}
                              value={rosenummer}
                              onChange={(e) => handleRosenummerChange(index, e.target.value)}
                              placeholder="Legg inn Rosenummer."
                            />
                          </div>
                          {(formData.rosenumre || ['']).length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRosenummer(index)}
                              style={{
                                background: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                height: '40px',
                                marginTop: '10px',
                                flexShrink: 0
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addRosenummer}
                        style={{
                          background: '#005379',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        ➕ Legg til ekstra rosenummer
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Display-specific fields - only shown for Display */}
              {(formData.grafiskProduksjon as string[]).includes('display') && (
                <>
                  <div className="form-row">
                    <div className="form-col full">
                      <h4 style={{ marginBottom: '16px', color: '#005379' }}>Display typer</h4>
                      <div className="checkbox-grid">
                        <CheckboxComponent
                          label="Flatfil"
                          name="displayTyper"
                          value="flatfil"
                          checked={(formData.displayTyper as string[]).includes('flatfil')}
                          onChange={(e) => handleCheckboxArrayChange('displayTyper', 'flatfil', e.target.checked)}
                        />
                        <CheckboxComponent
                          label="Karusell"
                          name="displayTyper"
                          value="karusell"
                          checked={(formData.displayTyper as string[]).includes('karusell')}
                          onChange={(e) => handleCheckboxArrayChange('displayTyper', 'karusell', e.target.checked)}
                        />
                        <CheckboxComponent
                          label="Bevegelig annonse"
                          name="displayTyper"
                          value="bevegeligAnnonse"
                          checked={(formData.displayTyper as string[]).includes('bevegeligAnnonse')}
                          onChange={(e) => handleCheckboxArrayChange('displayTyper', 'bevegeligAnnonse', e.target.checked)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <h4 style={{ marginBottom: '16px', color: '#005379' }}>
                        Displayformat <span style={{ color: '#dc3545' }}>*</span>
                      </h4>
                      <div className="checkbox-grid">
                        <CheckboxComponent
                          label="Netboard Board XL (580x500/320x400)"
                          name="displayformat"
                          value="netboardXL"
                          checked={(formData.displayformat as string[]).includes('netboardXL')}
                          onChange={(e) => handleCheckboxArrayChange('displayformat', 'netboardXL', e.target.checked)}
                        />
                        <CheckboxComponent
                          label="Netboard Board (580x500/320x250)"
                          name="displayformat"
                          value="netboard"
                          checked={(formData.displayformat as string[]).includes('netboard')}
                          onChange={(e) => handleCheckboxArrayChange('displayformat', 'netboard', e.target.checked)}
                        />
                      </div>
                      <div className="checkbox-grid">
                        <CheckboxComponent
                          label="Ukens stilling"
                          name="displayformat"
                          value="ukensStillingFormat"
                          checked={(formData.displayformat as string[]).includes('ukensStillingFormat')}
                          onChange={(e) => handleCheckboxArrayChange('displayformat', 'ukensStillingFormat', e.target.checked)}
                        />
                        <CheckboxComponent
                          label="Stilling Premium"
                          name="displayformat"
                          value="stillingPremiumFormat"
                          checked={(formData.displayformat as string[]).includes('stillingPremiumFormat')}
                          onChange={(e) => handleCheckboxArrayChange('displayformat', 'stillingPremiumFormat', e.target.checked)}
                        />
                        <CheckboxComponent
                          label="Andre formater"
                          name="displayformat"
                          value="andreFormater"
                          checked={(formData.displayformat as string[]).includes('andreFormater')}
                          onChange={(e) => handleCheckboxArrayChange('displayformat', 'andreFormater', e.target.checked)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Stillingskarusell conditional fields */}
              {(formData.grafiskProduksjon as string[]).includes('stillingskarusell') && (
                <div style={{
                  border: '2px solid #005379',
                  borderRadius: '8px',
                  padding: '20px',
                  marginTop: '20px',
                  backgroundColor: '#f8fafc'
                }}>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    color: '#005379',
                    fontSize: '18px',
                    fontWeight: '600',
                    textAlign: 'center',
                    borderBottom: '1px solid #005379',
                    paddingBottom: '8px'
                  }}>
                    Stillingskarusell
                  </h4>

                  <div className="form-row">
                    <div className="form-col half">
                      <label style={{ display: 'block', marginTop: '10px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                        Fra <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="date"
                        name="stillingskarusellFra"
                        value={formData.stillingskarusellFra}
                        onChange={(e) => handleGrafiskDateChange('stillingskarusellFra', e.target.value)}
                        min={getTodayDate()}
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '16px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    <div className="form-col half">
                      <label style={{ display: 'block', marginTop: '10px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                        Til <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="date"
                        name="stillingskarusellTil"
                        value={formData.stillingskarusellTil}
                        onChange={(e) => handleGrafiskDateChange('stillingskarusellTil', e.target.value)}
                        min={formData.stillingskarusellFra || getTodayDate()}
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '16px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col full">
                      <CheckboxComponent
                        label="Skal på MN24.no"
                        name="skalPaMn24"
                        value="true"
                        checked={formData.skalPaMn24}
                        onChange={(e) => handleInputChange(e)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Brief
                  </label>
                  <textarea
                    name="hvissAnnet"
                    value={formData.hvissAnnet}
                    onChange={handleInputChange}
                    placeholder="Legg ved en brief til din bestilling"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>

              {/* Korrektur Mail Section */}
              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                Korrektur Mail
              </h3>

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Korrektur dato <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="korrekturDato"
                    value={formData.korrekturDato}
                    onChange={handleInputChange}
                    min={getTodayDate()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  <label style={{ display: 'flex', alignItems: 'center', marginTop: '20px', marginBottom: '8px', fontWeight: '600', color: '#005379' }}>
                    Korrektur E-post <span style={{ color: '#dc3545' }}>*</span>
                    <InfoTooltip text='Du kan legge til flere E-poster ved å separere disse med komma ","' />
                  </label>
                  <input
                    type="email"
                    name="korrekturEpost"
                    value={formData.korrekturEpost}
                    onChange={handleInputChange}
                    placeholder="epost@example.com, epost2@example.com"
                    required
                    multiple
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
              </div>

              {/* Rapport Section */}
              <h3 style={{ margin: '32px 0 24px 0', color: '#005379', fontSize: '20px', fontWeight: '600' }}>
                Rapport
              </h3>

              <div className="form-row">
                <div className="form-col full">
                  <CheckboxComponent
                    label="Rapport kunde"
                    name="rapportKunde"
                    value="true"
                    checked={formData.rapportKunde}
                    onChange={(e) => handleInputChange(e)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-col full">
                  <CheckboxComponent
                    label="Rapport selger"
                    name="rapportSelger"
                    value="true"
                    checked={formData.rapportSelger}
                    onChange={(e) => handleInputChange(e)}
                  />
                </div>
              </div>
            </div>
          </form>

          {/* Form Actions */}
          <div className="form-actions-section">
            {isDevHost && (
              <div className="dev-integration-toggles">
                <p className="dev-integration-toggles-title">Dev: eksterne utsendinger</p>
                <label className="dev-integration-toggle">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Send e-post</span>
                </label>
                <label className="dev-integration-toggle">
                  <input
                    type="checkbox"
                    checked={sendAsana}
                    onChange={(e) => setSendAsana(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Opprett Asana-oppgave</span>
                </label>
                <p className="dev-integration-toggles-note">
                  Av som standard, så testdata ikke går til studio-inbox eller Asana.
                </p>
              </div>
            )}
            <div className="form-actions-buttons">
              <button
                className="btn btn-secondary"
                onClick={handlePDFPreview}
                disabled={isSubmitting}
              >
                📄 Forhåndsvis skjema
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitWithValidation}
                disabled={isSubmitting}
              >
                {isSubmitting ? '📤 Sender...' : '📤 Send bestilling'}
              </button>
              {isSubmitted && (
                <button
                  className="btn btn-warning"
                  onClick={() => {
                    const confirmReset = window.confirm('Vil du tilbakestille skjemaet? All data vil gå tapt.');
                    if (confirmReset) {
                      handleFormReset();
                    }
                  }}
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    marginLeft: '10px'
                  }}
                >
                  🔄 Resett skjema
                </button>
              )}
            </div>
            <p className="form-actions-note">
              {isDevHost
                ? `PDF lagres alltid. E-post ${sendEmail ? 'sendes' : 'sendes ikke'}. Asana ${sendAsana ? 'opprettes' : 'opprettes ikke'}.`
                : 'Ved å sende inn skjemaet genereres en PDF som sendes via e-post til relevante parter.'}
              {isSubmitted && ' Skjemaet er sendt inn - du kan nå tilbakestille det for å lage en ny bestilling.'}
            </p>
          </div>
        </div>
      </div>

      {/* Floating Template Menu */}
      <FloatingTemplateMenu
        onSaveTemplate={handleSaveTemplate}
        onLoadTemplate={handleLoadTemplate}
        currentFormData={formData}
      />

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={showPDFPreview}
        onClose={() => setShowPDFPreview(false)}
        formData={formData}
      />
    </div>
  );
};

export default FormPage;
